import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Avatar,
  Typography
} from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import { Model, ModelFormData, Brand } from '../../types';
import equipmentService from '../../services/equipmentService';

interface ModelFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  model?: Model | null;
}

const ModelForm: React.FC<ModelFormProps> = ({
  open,
  onClose,
  onSuccess,
  model
}) => {
  const [formData, setFormData] = useState<ModelFormData>({
    marcaId: 0,
    titulo: '',
    tipo: 'Roteador',
    portas_lan: 1,
    frequencia_wifi: '',
    portas_fxs: 0
  });
  const [brands, setBrands] = useState<Brand[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tiposEquipamento = [
    { value: 'ONU', label: 'ONU' },
    { value: 'ONT', label: 'ONT' },
    { value: 'Roteador', label: 'Roteador' },
    { value: 'Switch', label: 'Switch' }
  ];

  const frequenciasWifi = [
    { value: '2.4GHz', label: '2.4GHz' },
    { value: '5GHz', label: '5GHz' },
    { value: '6GHz', label: '6GHz (WiFi 6E)' },
    { value: 'Dual Band', label: 'Dual Band (2.4GHz + 5GHz)' },
    { value: 'Tri Band', label: 'Tri Band (2.4GHz + 5GHz + 6GHz)' }
  ];

  // Carregar marcas
  useEffect(() => {
    if (open) {
      loadBrands();
    }
  }, [open]);

  const loadBrands = async () => {
    try {
      const response = await equipmentService.getBrands();
      if (response.success && response.data) {
        setBrands(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar marcas:', error);
    }
  };

  const handleInputChange = (field: keyof ModelFormData, value: any) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };

      // Limpar frequências WiFi quando tipo não for ONT ou Roteador
      if (field === 'tipo' && value !== 'ONT' && value !== 'Roteador') {
        newData.frequencia_wifi = '';
      }

      return newData;
    });
    
    // Limpar erro quando o usuário começar a digitar
    if (error) {
      setError(null);
    }
  };



  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar tamanho (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('A imagem deve ter no máximo 5MB');
        return;
      }

      // Validar tipo
      if (!file.type.startsWith('image/')) {
        setError('Apenas arquivos de imagem são aceitos');
        return;
      }

      setFormData(prev => ({
        ...prev,
        imagem: file
      }));

      // Criar preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = (): boolean => {
    if (!formData.marcaId) {
      setError('A marca é obrigatória');
      return false;
    }

    if (!formData.titulo.trim()) {
      setError('O título é obrigatório');
      return false;
    }

    if (formData.titulo.trim().length < 2) {
      setError('O título deve ter pelo menos 2 caracteres');
      return false;
    }

    if (!formData.tipo) {
      setError('O tipo é obrigatório');
      return false;
    }

    if (formData.portas_lan < 1) {
      setError('A quantidade de portas LAN deve ser maior que 0');
      return false;
    }

    if (formData.portas_fxs && formData.portas_fxs < 0) {
      setError('A quantidade de portas FXS deve ser maior ou igual a 0');
      return false;
    }

    // Validar frequência WiFi para ONT e Roteador
    if ((formData.tipo === 'ONT' || formData.tipo === 'Roteador') && 
        (!formData.frequencia_wifi || formData.frequencia_wifi.trim() === '')) {
      setError('A frequência WiFi é obrigatória para equipamentos ONT e Roteador');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const dataToSend: any = {
        marcaId: formData.marcaId,
        titulo: formData.titulo.trim(),
        tipo: formData.tipo,
        portas_lan: formData.portas_lan,
        frequencia_wifi: formData.frequencia_wifi && formData.frequencia_wifi.trim() !== '' 
          ? formData.frequencia_wifi.trim() 
          : undefined,
        portas_fxs: formData.portas_fxs || 0,
        imagem: formData.imagem
      };

      if (model) {
        await equipmentService.updateModel(model.id, dataToSend);
      } else {
        await equipmentService.createModel(dataToSend);
      }

      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Erro ao salvar modelo:', error);
      setError(error?.response?.data?.message || 'Erro ao salvar modelo');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      marcaId: 0,
      titulo: '',
      tipo: 'Roteador',
      portas_lan: 1,
      frequencia_wifi: '',
      portas_fxs: 0
    });
    setImagePreview(null);
    setError(null);
    onClose();
  };

  // Resetar form quando abrir para edição
  useEffect(() => {
    if (open && model) {
      setFormData({
        marcaId: model.marcaId,
        titulo: model.titulo,
        tipo: model.tipo as 'ONU' | 'ONT' | 'Roteador' | 'Switch',
        portas_lan: model.portas_lan,
        frequencia_wifi: model.frequencia_wifi || '',
        portas_fxs: model.portas_fxs || 0
      });
      
      // Definir preview da imagem existente
      if (model.imagem) {
        setImagePreview(equipmentService.getModelImageUrl(model.imagem));
      }
    } else if (open && !model) {
              setFormData({
          marcaId: 0,
          titulo: '',
          tipo: 'Roteador',
          portas_lan: 1,
          frequencia_wifi: '',
          portas_fxs: 0
        });
      setImagePreview(null);
    }
  }, [open, model]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ p: 1.5 }}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            {model ? 'Editar Modelo' : 'Cadastrar Modelo'}
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 1.5 }}>
          <Box sx={{ pt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={1.5}>
              {/* Marca */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Marca</InputLabel>
                  <Select
                    value={formData.marcaId}
                    onChange={(e) => handleInputChange('marcaId', e.target.value)}
                    label="Marca"
                    size="small"
                    disabled={loading}
                  >
                    <MenuItem value={0}>Selecione uma marca</MenuItem>
                    {brands.map((brand) => (
                      <MenuItem key={brand.id} value={brand.id}>
                        {brand.titulo}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Tipo */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={formData.tipo}
                    onChange={(e) => handleInputChange('tipo', e.target.value)}
                    label="Tipo"
                    size="small"
                    disabled={loading}
                  >
                    {tiposEquipamento.map((tipo) => (
                      <MenuItem key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Título */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Título do Modelo"
                  value={formData.titulo}
                  onChange={(e) => handleInputChange('titulo', e.target.value)}
                  required
                  placeholder="Ex: Archer C6, HG8245H, EX220..."
                  helperText="Nome do modelo do equipamento"
                  disabled={loading}
                  size="small"
                />
              </Grid>

              {/* Quantidade de Portas LAN */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Qtde Portas LAN"
                  value={formData.portas_lan}
                  onChange={(e) => handleInputChange('portas_lan', parseInt(e.target.value) || 0)}
                  required
                  inputProps={{ min: 0 }}
                  size="small"
                  helperText="Número de portas Ethernet"
                  disabled={loading}
                />
              </Grid>

              {/* Quantidade de Portas FXS */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Qtde Portas FXS"
                  value={formData.portas_fxs}
                  onChange={(e) => handleInputChange('portas_fxs', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0 }}
                  size="small"
                  helperText="Número de portas para telefone (opcional)"
                  disabled={loading}
                />
              </Grid>

              {/* Frequência WiFi - Select Simples - Apenas para ONT e Roteador */}
              {(formData.tipo === 'ONT' || formData.tipo === 'Roteador') && (
                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Frequência WiFi *</InputLabel>
                    <Select
                      value={formData.frequencia_wifi || ''}
                      onChange={(e) => handleInputChange('frequencia_wifi', e.target.value)}
                      label="Frequência WiFi *"
                      disabled={loading}
                      size="small"
                    >
                      <MenuItem value="">Selecione uma frequência</MenuItem>
                      {frequenciasWifi.map((freq) => (
                        <MenuItem key={freq.value} value={freq.value}>
                          {freq.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                      Selecione a frequência WiFi suportada (obrigatório para ONT e Roteador)
                    </Typography>
                  </FormControl>
                </Grid>
              )}

              {/* Upload de Imagem */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    src={imagePreview || undefined}
                    sx={{ width: 80, height: 80 }}
                  >
                    <PhotoCamera />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Imagem do Equipamento (opcional)
                    </Typography>
                    <input
                      accept="image/*"
                      type="file"
                      onChange={handleImageChange}
                      disabled={loading}
                      style={{ marginTop: 8 }}
                    />
                    <Typography variant="caption" display="block" color="textSecondary">
                      Formatos aceitos: JPG, PNG, GIF. Máximo 5MB.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Salvando...' : model ? 'Atualizar' : 'Cadastrar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ModelForm; 